import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import {
  Plus, Pencil, Trash2, X, BookOpen, CalendarCheck, Mail, Phone, MapPin, Hash, Building2, Wallet, ExternalLink,
  GraduationCap, CheckCircle2, Clock, Sparkles, ArrowUpRight, TrendingUp,
} from "lucide-react";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";

const COURSE = {
  google_ads: { label: "Google Ads", short: "GA", theme: "blue" },
  meta_business: { label: "Meta Business", short: "MB", theme: "purple" },
  karma: { label: "Karma Program", short: "KP", theme: "indigo" },
};

const THEMES = {
  blue: {
    accent: "#2563EB", soft: "#EFF6FF", chip: "bg-blue-50 text-blue-700 border-blue-200",
    avatar: "bg-gradient-to-br from-blue-500 to-blue-700",
    bar: "from-blue-400 via-blue-500 to-blue-600",
    ring: "ring-blue-100", topbar: "bg-blue-500",
  },
  purple: {
    accent: "#9333EA", soft: "#FAF5FF", chip: "bg-purple-50 text-purple-700 border-purple-200",
    avatar: "bg-gradient-to-br from-fuchsia-500 to-purple-700",
    bar: "from-fuchsia-400 via-purple-500 to-purple-600",
    ring: "ring-purple-100", topbar: "bg-purple-500",
  },
  indigo: {
    accent: "#4F46E5", soft: "#EEF2FF", chip: "bg-indigo-50 text-indigo-700 border-indigo-200",
    avatar: "bg-gradient-to-br from-indigo-500 to-purple-600",
    bar: "from-indigo-400 via-violet-500 to-purple-600",
    ring: "ring-indigo-100", topbar: "bg-gradient-to-r from-indigo-500 to-purple-500",
  },
};

const EMPTY = { name: "", email: "", phone: "", address: "", tax_id: "", company: "", billing_address: "", course: "google_ads", lessons_sold: 0, total_amount: 0, paid_amount: 0, notes: "" };

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || "").join("") || "?";
const tr = (n) => Number(n || 0).toLocaleString("tr-TR");
const themeOf = (course) => THEMES[(COURSE[course]?.theme) || "indigo"];

export default function StudentsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [tab, setTab] = useState("all");

  const load = () => apiClient.get("/students").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const p = {
        ...form, email: form.email || null,
        lessons_sold: Number(form.lessons_sold) || 0,
        total_amount: Number(form.total_amount) || 0,
        paid_amount: Number(form.paid_amount) || 0,
      };
      if (editing) await apiClient.put(`/students/${editing.id}`, p);
      else await apiClient.post("/students", p);
      setOpen(false); load();
    } catch (er) { setErr(formatApiError(er)); }
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await apiClient.delete(`/students/${id}`); load(); };
  const openDetail = async (s) => {
    setDetail(s);
    try { const { data } = await apiClient.get(`/students/${s.id}/detail`); setDetailData(data); }
    catch (e) { setDetailData(null); }
  };

  const kpis = useMemo(() => {
    const doneOf = (s) => s.lessons_active_count || 0;
    const total = items.length;
    const activeS = items.filter(s => (s.lessons_sold || 0) > doneOf(s)).length;
    const completed = items.filter(s => (s.lessons_sold || 0) > 0 && doneOf(s) >= (s.lessons_sold || 0)).length;
    const totalSold = items.reduce((sum, s) => sum + (s.lessons_sold || 0), 0);
    const totalDone = items.reduce((sum, s) => sum + doneOf(s), 0);
    const totalRev = items.reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
    const paidRev = items.reduce((sum, s) => sum + Number(s.paid_amount || 0), 0);
    const completionRate = totalSold ? Math.round((totalDone / totalSold) * 100) : 0;
    return { total, activeS, completed, totalSold, totalDone, completionRate, totalRev, paidRev, pendingRev: totalRev - paidRev };
  }, [items]);

  const filteredItems = useMemo(() => {
    const doneOf = (s) => s.lessons_active_count || 0;
    if (tab === "aktif") return items.filter(s => (s.lessons_sold || 0) > doneOf(s));
    if (tab === "tamamlanan") return items.filter(s => (s.lessons_sold || 0) > 0 && doneOf(s) >= (s.lessons_sold || 0));
    if (tab === "odeme_bekleyen") return items.filter(s => Number(s.total_amount || 0) > Number(s.paid_amount || 0));
    return items;
  }, [items, tab]);

  return (
    <div className="space-y-7" data-testid="students-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600/80">
            <Sparkles size={12} /> EĞİTİM MERKEZİ
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-1">Öğrenciler</h1>
          <p className="text-sm text-slate-500 mt-1.5">
            <span className="font-semibold text-slate-700">{items.length}</span> öğrenci ·
            <span className="font-semibold text-emerald-600 mx-1">%{kpis.completionRate}</span> tamamlanma oranı ·
            <span className="font-mono ml-1">{kpis.totalDone}/{kpis.totalSold}</span> ders
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav("/dersler")} className="ky-btn ky-btn-outline group" data-testid="goto-lessons">
            <BookOpen size={14} />Dersler<ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
          <button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="ky-btn ky-btn-accent" data-testid="add-student-btn">
            <Plus size={14} />Yeni Öğrenci
          </button>
        </div>
      </div>

      {/* KPI Grid — premium cards with radial chart + neon accent border */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiPremium label="Aktif Öğrenci" value={kpis.activeS} total={kpis.total} accent="#4F46E5" Icon={GraduationCap} sub={`${kpis.total} toplam`} testid="kpi-active" onClick={() => setTab("aktif")}/>
        <KpiPremium label="Tamamlanan" value={kpis.completed} total={kpis.total} accent="#10B981" Icon={CheckCircle2} sub="öğrenci" testid="kpi-completed" onClick={() => setTab("tamamlanan")}/>
        <KpiPremium label="Tamamlanma" value={kpis.completionRate} total={100} accent="#F59E0B" Icon={TrendingUp} sub="oranı" suffix="%" testid="kpi-rate"/>
        <KpiPremium label="Bekleyen Tahsilat" value={Math.round(kpis.pendingRev)} accent="#EF4444" Icon={Wallet} sub={`${tr(kpis.paidRev)} ₺ ödenmiş`} testid="kpi-pending" isMoney onClick={() => setTab("odeme_bekleyen")}/>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="students-tabs">
        {[
          ["all", "Tümü", items.length],
          ["aktif", "Aktif", kpis.activeS],
          ["tamamlanan", "Tamamlanan", kpis.completed],
          ["odeme_bekleyen", "Ödeme Bekleyen", items.filter(s => Number(s.total_amount || 0) > Number(s.paid_amount || 0)).length],
        ].map(([k, l, n]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap flex items-center gap-2 transition-all duration-200 ${tab === k ? "border-indigo-500 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-900"}`} data-testid={`tab-${k}`}>
            {l}<span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums ${tab === k ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>{n}</span>
          </button>
        ))}
      </div>

      {/* Student Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filteredItems.map((s, i) => (
          <StudentCard key={s.id} s={s} index={i} onOpen={() => openDetail(s)} onEdit={() => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); }} onDel={() => del(s.id)} />
        ))}
        {filteredItems.length === 0 && <EmptyState onAdd={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} />}
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex" onClick={() => { setDetail(null); setDetailData(null); }}>
          <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" />
          <div onClick={e => e.stopPropagation()} className="w-full sm:w-[680px] bg-white/95 backdrop-blur-2xl border-l border-slate-200 flex flex-col ky-slide-in shadow-2xl" data-testid="student-detail">
            <DetailHeader student={detail} onClose={() => { setDetail(null); setDetailData(null); }} />
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <ContactGrid s={detail} />
              <PaymentSummary stats={detailData?.stats} fallback={detail} />
              <LessonStatsGrid stats={detailData?.stats} fallback={detail} />
              <button onClick={() => nav("/dersler")} className="w-full ky-btn ky-btn-accent group" data-testid="goto-lessons-page">
                <Plus size={14} />Yeni Ders Planla<span className="text-[10px] opacity-70 ml-auto">/dersler</span>
              </button>
              <LessonTimeline data={detailData} />
            </div>
          </div>
        </div>
      )}

      {/* Form modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setOpen(false)}>
          <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" />
          <form onSubmit={save} onClick={e => e.stopPropagation()} className="w-full sm:w-[560px] bg-white border-l border-slate-200 flex flex-col ky-slide-in shadow-2xl" data-testid="student-form">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold tracking-tight">{editing ? "Öğrenci Düzenle" : "Yeni Öğrenci"}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{editing ? "Bilgileri güncelle" : "Eğitim sürecini başlat"}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {err && <div className="text-sm px-4 py-3 rounded-xl bg-red-50 text-red-600 border border-red-200">{err}</div>}
              <Section title="Kişisel Bilgiler">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ad Soyad *" full><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="ky-input" data-testid="student-input-name" /></Field>
                  <Field label="E-posta"><input type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} className="ky-input" /></Field>
                  <Field label="Telefon"><input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} className="ky-input" /></Field>
                  <Field label="Şirket"><input value={form.company || ""} onChange={e => setForm({ ...form, company: e.target.value })} className="ky-input" data-testid="student-input-company" /></Field>
                  <Field label="VKN / TCKN"><input value={form.tax_id || ""} onChange={e => setForm({ ...form, tax_id: e.target.value })} className="ky-input font-mono" data-testid="student-input-tax" /></Field>
                  <Field label="Adres" full><input value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} className="ky-input" /></Field>
                  <Field label="Fatura Adresi (opsiyonel)" full><input value={form.billing_address || ""} onChange={e => setForm({ ...form, billing_address: e.target.value })} className="ky-input" data-testid="student-input-billing" /></Field>
                </div>
              </Section>
              <Section title="Eğitim & Ödeme">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Eğitim Programı *" full>
                    <select value={form.course} onChange={e => setForm({ ...form, course: e.target.value })} className="ky-select" data-testid="student-input-course">
                      {Object.entries(COURSE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Satılan Ders" full><input type="number" value={form.lessons_sold} onChange={e => setForm({ ...form, lessons_sold: e.target.value })} className="ky-input font-mono" /></Field>
                  <Field label="Toplam (₺)"><input type="number" value={form.total_amount} onChange={e => setForm({ ...form, total_amount: e.target.value })} className="ky-input font-mono" /></Field>
                  <Field label="Ödenen (₺)"><input type="number" value={form.paid_amount} onChange={e => setForm({ ...form, paid_amount: e.target.value })} className="ky-input font-mono" /></Field>
                  <Field label="Notlar" full><textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} className="ky-textarea" rows={3} /></Field>
                  <div className="col-span-2 text-[10px] text-slate-400 -mt-1 px-1 leading-relaxed">
                    💡 Sonraki ders bilgisi <b>Dersler</b> sayfasından otomatik beslenir — bir ders eklendiğinde öğrenci kartında otomatik görünür, dersin tarihi geçtiğinde kaybolur.
                  </div>
                </div>
              </Section>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-2">
              <button type="button" onClick={() => setOpen(false)} className="ky-btn ky-btn-outline flex-1">İptal</button>
              <button type="submit" className="ky-btn ky-btn-accent flex-1" data-testid="save-student">{editing ? "Güncelle" : "Kaydet"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ============ Sub-components ============

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-3">{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function KpiPremium({ label, value, total, accent, Icon, sub, suffix = "", testid, onClick, isMoney }) {
  const pct = total ? Math.round((value / total) * 100) : 100;
  const chartData = [{ name: "v", value: pct, fill: accent }];
  return (
    <button onClick={onClick} className="relative bg-white rounded-2xl border border-slate-200 p-5 text-left overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-300 group" data-testid={testid} style={{ cursor: onClick ? "pointer" : "default" }}>
      {/* Top accent bar */}
      <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}99)` }} />
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: `${accent}15`, color: accent }}>
          <Icon size={14} />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-3xl font-black tabular-nums tracking-tight" style={{ color: accent }}>
            {isMoney ? tr(value) : value}
            <span className="text-base font-bold ml-0.5">{isMoney ? " ₺" : suffix}</span>
          </div>
          {sub && <div className="text-[11px] text-slate-400 font-medium mt-0.5">{sub}</div>}
        </div>
        {/* Mini radial */}
        <div className="w-14 h-14 -mr-2">
          <ResponsiveContainer>
            <RadialBarChart innerRadius="68%" outerRadius="100%" data={chartData} startAngle={90} endAngle={-270}>
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar background={{ fill: "#F1F5F9" }} dataKey="value" cornerRadius={8} fill={accent} />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </button>
  );
}

function StudentCard({ s, index, onOpen, onEdit, onDel }) {
  const course = COURSE[s.course] || COURSE.google_ads;
  const t = themeOf(s.course);
  const sold = s.lessons_sold || 0;
  // Progress = active (non-cancelled) lessons. Starts at 0 and grows with each lesson added.
  const done = s.lessons_active_count || 0;
  const remaining = Math.max(0, sold - done);
  const progressPct = sold ? Math.min(100, Math.round((done / sold) * 100)) : 0;
  const moneyRem = Math.max(0, Number(s.total_amount || 0) - Number(s.paid_amount || 0));
  const isPaid = moneyRem === 0 && Number(s.total_amount || 0) > 0;

  // Animation delay for stagger
  const style = { animationDelay: `${Math.min(index * 60, 480)}ms` };

  return (
    <div
      className="relative bg-white rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300 ky-fade-in"
      data-testid={`student-card-${s.id}`}
      style={style}
    >
      {/* Course-themed top bar */}
      <div className={`absolute inset-x-0 top-0 h-1 ${t.topbar}`} />

      <div className="p-6 pb-4">
        {/* Header: avatar + name */}
        <div className="flex items-start gap-3 mb-4">
          <button onClick={onOpen} className={`shrink-0 w-12 h-12 rounded-xl ${t.avatar} flex items-center justify-center text-white font-black text-sm shadow-md ${t.ring} ring-4 hover:scale-105 transition-transform`} data-testid={`avatar-${s.id}`}>
            {initials(s.name)}
          </button>
          <div className="flex-1 min-w-0">
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${t.chip} border mb-1`}>
              <BookOpen size={9} />{course.label}
            </span>
            <button onClick={onOpen} className="block w-full text-left">
              <h3 className="text-base font-bold text-slate-900 truncate hover:text-indigo-600 transition-colors">{s.name}</h3>
            </button>
            {s.company && <div className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5"><Building2 size={9} />{s.company}</div>}
          </div>
        </div>

        {/* Contact micro-row */}
        <div className="space-y-1 mb-4 text-[11px] text-slate-500">
          {s.email && <div className="flex items-center gap-1.5 truncate"><Mail size={10} className="shrink-0" />{s.email}</div>}
          {s.phone && <div className="flex items-center gap-1.5"><Phone size={10} />{s.phone}</div>}
        </div>

        {/* Dot matrix progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">İlerleme</span>
            <span className="text-[10px] font-mono font-bold" style={{ color: t.accent }}>{progressPct}%</span>
          </div>
          <DotMatrix done={done} total={sold} accent={t.accent} />
          <div className="flex items-center justify-between mt-2 text-[10px]">
            <span className="font-mono text-slate-700"><b className="text-slate-900">{done}</b>/{sold}</span>
            <span className="font-mono text-slate-400">{remaining > 0 ? `${remaining} kaldı` : "✓ Tamamlandı"}</span>
          </div>
        </div>

        {/* Payment row */}
        <div className="border-t border-slate-100 pt-3 mb-4">
          <div className="flex items-center justify-between text-[11px]">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Ödenen</div>
              <div className="text-sm font-bold tabular-nums text-emerald-600 mt-0.5">{tr(s.paid_amount)} ₺</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{isPaid ? "Durum" : "Kalan"}</div>
              <div className={`text-sm font-bold tabular-nums mt-0.5 ${isPaid ? "text-emerald-500" : moneyRem > 0 ? "text-red-500" : "text-slate-400"}`}>
                {isPaid ? "Ödendi ✓" : `${tr(moneyRem)} ₺`}
              </div>
            </div>
          </div>
          {s.next_upcoming_lesson_at && (
            <div className="mt-3 flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg" style={{ background: t.soft, color: t.accent }}>
              <CalendarCheck size={11} />
              <span className="font-medium">Sonraki ders: {new Date(s.next_upcoming_lesson_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action footer */}
      <div className="bg-slate-50 px-6 py-3 flex items-center gap-2 border-t border-slate-100">
        <button onClick={onOpen} className="ky-btn ky-btn-outline ky-btn-sm flex-1" data-testid={`open-student-${s.id}`}><BookOpen size={11} />Detay</button>
        <button onClick={onEdit} className="ky-btn ky-btn-ghost ky-btn-sm" title="Düzenle" data-testid={`edit-student-${s.id}`}><Pencil size={11} /></button>
        <button onClick={onDel} className="ky-btn ky-btn-ghost ky-btn-sm text-red-500" title="Sil" data-testid={`del-student-${s.id}`}><Trash2 size={11} /></button>
      </div>
    </div>
  );
}

function DotMatrix({ done, total, accent }) {
  if (!total) return <div className="h-2 rounded-full bg-slate-100" />;
  const max = Math.min(total, 20); // Cap to 20 dots; if more, show condensed bar
  if (total > 20) {
    const pct = total ? (done / total) * 100 : 0;
    return (
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${accent}, ${accent}cc)` }} />
      </div>
    );
  }
  return (
    <div className="flex gap-1">
      {Array.from({ length: max }).map((_, i) => {
        const isDone = i < done;
        return (
          <div key={i} className="flex-1 h-2 rounded-sm transition-all duration-300" style={{ background: isDone ? accent : "#E2E8F0", transform: isDone ? "scaleY(1)" : "scaleY(0.7)" }} />
        );
      })}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="col-span-full">
      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/40 via-transparent to-purple-50/40 pointer-events-none" />
        <div className="relative">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 items-center justify-center text-white shadow-lg shadow-indigo-200 mb-4">
            <GraduationCap size={28} />
          </div>
          <h3 className="text-lg font-bold tracking-tight">Bu filtrede öğrenci yok</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Filtreyi değiştir veya yeni bir öğrenci ekleyerek başla.</p>
          <button onClick={onAdd} className="ky-btn ky-btn-accent mt-5" data-testid="empty-add"><Plus size={14} />İlk Öğrenciyi Ekle</button>
        </div>
      </div>
    </div>
  );
}

function DetailHeader({ student, onClose }) {
  const course = COURSE[student.course] || COURSE.google_ads;
  const t = themeOf(student.course);
  return (
    <div className="relative px-6 py-5 border-b border-slate-100 overflow-hidden">
      <div className={`absolute inset-0 opacity-40 ${t.topbar}`} style={{ filter: "blur(40px)", transform: "translateY(-50%)" }} />
      <div className="relative flex items-start gap-4">
        <div className={`w-14 h-14 rounded-2xl ${t.avatar} flex items-center justify-center text-white font-black text-base shadow-md`}>
          {initials(student.name)}
        </div>
        <div className="flex-1 min-w-0">
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${t.chip} border`}>
            <BookOpen size={9} />{course.label}
          </span>
          <h3 className="text-xl font-bold tracking-tight text-slate-900 mt-1 truncate">{student.name}</h3>
          {student.company && <div className="text-xs text-slate-500 truncate mt-0.5">{student.company}</div>}
        </div>
        <button onClick={onClose} className="ky-btn ky-btn-ghost ky-btn-icon shrink-0"><X size={14} /></button>
      </div>
    </div>
  );
}

function ContactGrid({ s }) {
  const rows = [
    s.email && { Icon: Mail, label: s.email },
    s.phone && { Icon: Phone, label: s.phone },
    s.tax_id && { Icon: Hash, label: `VKN: ${s.tax_id}` },
    s.address && { Icon: MapPin, label: s.address, full: true },
    s.billing_address && { Icon: Building2, label: `Fatura: ${s.billing_address}`, full: true },
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <div className="grid grid-cols-2 gap-3">
      {rows.map((r, i) => {
        const Icon = r.Icon;
        return (
          <div key={i} className={`flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 ${r.full ? "col-span-2" : ""}`}>
            <Icon size={12} className="text-slate-400 shrink-0" />
            <span className="truncate">{r.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function PaymentSummary({ stats, fallback }) {
  const total = stats?.total_amount ?? fallback?.total_amount ?? 0;
  const paid = stats?.paid_amount ?? fallback?.paid_amount ?? 0;
  const remaining = stats?.remaining_amount ?? Math.max(0, total - paid);
  const paidPct = total ? Math.round((paid / total) * 100) : 0;
  return (
    <div className="relative bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-100 rounded-2xl p-5 overflow-hidden">
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-indigo-200/30 blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-indigo-600 text-white flex items-center justify-center"><Wallet size={13} /></div>
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-700">Ödeme Özeti</span>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <MoneyStat label="Toplam" value={total} color="text-slate-900" />
          <MoneyStat label="Ödenen" value={paid} color="text-emerald-600" />
          <MoneyStat label="Kalan" value={remaining} color={remaining > 0 ? "text-red-500" : "text-slate-400"} />
        </div>
        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-700" style={{ width: `${paidPct}%` }} />
        </div>
        <div className="text-[10px] font-mono text-slate-500 mt-1.5 text-right">%{paidPct} tahsil edildi</div>
      </div>
    </div>
  );
}

function MoneyStat({ label, value, color }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-base font-black tabular-nums mt-0.5 ${color}`}>{tr(value)} <span className="text-xs font-bold opacity-70">₺</span></div>
    </div>
  );
}

function LessonStatsGrid({ stats, fallback }) {
  const sold = stats?.lessons_sold ?? fallback?.lessons_sold ?? 0;
  // v13: All counts now derived from real lessons records (not the legacy lessons_done counter)
  const done = stats?.lessons_completed ?? 0;
  const planned = stats?.lessons_planned ?? 0;
  const total = stats?.lessons_total ?? 0;
  const cells = [
    { label: "Satılan", value: sold, color: "#0F172A", Icon: ShoppingBagSafe },
    { label: "Tamamlanan", value: done, color: "#10B981", Icon: CheckCircle2 },
    { label: "Planlanan", value: planned, color: "#4F46E5", Icon: Clock },
    { label: "Toplam", value: total, color: "#7C3AED", Icon: BookOpen },
  ];
  return (
    <div className="grid grid-cols-4 gap-2">
      {cells.map((c, i) => {
        const Icon = c.Icon;
        return (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-3 text-center hover:border-indigo-200 transition-colors">
            <div className="inline-flex w-7 h-7 rounded-lg items-center justify-center mb-1.5" style={{ background: `${c.color}15`, color: c.color }}>
              <Icon size={12} />
            </div>
            <div className="text-lg font-black tabular-nums" style={{ color: c.color }}>{c.value}</div>
            <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mt-0.5">{c.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function ShoppingBagSafe(props) { return <BookOpen {...props} />; }

function LessonTimeline({ data }) {
  const lessons = data?.lessons || [];
  const logs = (data?.lesson_logs || []).filter(log => !log.from_lesson_id);
  if (!lessons.length && !logs.length) {
    return (
      <div className="text-center py-8 px-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
        <BookOpen size={20} className="mx-auto text-slate-300 mb-2" />
        <div className="text-sm font-semibold text-slate-600">Henüz ders kaydı yok</div>
        <p className="text-xs text-slate-400 mt-1">"Yeni Ders Planla" diyerek başlayabilirsin.</p>
      </div>
    );
  }
  return (
    <div>
      <h4 className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-3">Ders Geçmişi & Plan</h4>
      <div className="space-y-2">
        {lessons.map(l => (
          <div key={l.id} className="bg-white border border-slate-200 rounded-xl p-3.5 hover:border-indigo-200 transition-colors" data-testid={`student-lesson-${l.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${l.status === "tamamlandi" ? "bg-emerald-100 text-emerald-600" : l.status === "iptal" ? "bg-slate-100 text-slate-500" : "bg-purple-100 text-purple-600"}`}>
                  <BookOpen size={11} />
                </div>
                <span className="font-bold text-sm truncate">{l.title}</span>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${l.status === "tamamlandi" ? "bg-emerald-100 text-emerald-700" : l.status === "iptal" ? "bg-slate-100 text-slate-500" : "bg-indigo-100 text-indigo-700"}`}>
                {l.status === "tamamlandi" ? "Tamamlandı" : l.status === "iptal" ? "İptal" : "Planlandı"}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5 ml-9">{new Date(l.start_at).toLocaleString("tr-TR")} · {l.duration_minutes || 60} dk</div>
            {l.topic && <div className="text-xs text-slate-600 mt-1 ml-9">{l.topic}</div>}
            {l.meeting_url && <a href={l.meeting_url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-600 mt-1 ml-9 inline-flex items-center gap-1 hover:underline"><ExternalLink size={10} />Meet</a>}
          </div>
        ))}
        {logs.map(log => (
          <div key={log.id} className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={11} /></div><span className="font-bold text-sm">{log.topic}</span></div>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">Tamamlandı</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5 ml-9">{new Date(log.date).toLocaleDateString("tr-TR")} · {log.duration_minutes || 60} dk · {log.instructor_name}</div>
            {log.notes && <div className="text-xs text-slate-600 mt-1 ml-9">{log.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
